import { Controller } from "@hotwired/stimulus";
import SignaturePad from "signature_pad";

export default class extends Controller {
  static targets = [
    "canvas",
    "input",
    "consentSection",
    "consentCheckbox",
    "consentText",
    "clearButton",
  ];

  static CONFIG = {
    RESIZE_DEBOUNCE: 50,
    TRANSITION_DELAY: 100,
    CANVAS_DELAY: 100,
    BREAKPOINTS: {
      SM: 640,
      MD: 768,
    },
    CANVAS_HEIGHTS: {
      XS: 120,
      SM: 150,
      MD: 200,
    },
    SIGNATURE_PAD_OPTIONS: {
      backgroundColor: "rgba(255, 255, 255, 1)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 0.5,
      maxWidth: 2.5,
      throttle: 16,
      minPointDistance: 3,
    },
    TEXT_OPTIONS: {
      fontFamily: "cursive, serif",
      color: "#000000",
      textAlign: "center",
      textBaseline: "middle",
      fontSizeRatio: 0.3,
    },
    RESIZE_OPTIONS: {
      useResizeObserver: true,
    },
    MODES: {
      DRAW: "draw",
      ADOPT: "adopt",
    },
  };

  connect() {
    this.currentMode = this.constructor.CONFIG.MODES.DRAW;

    this.initializeCanvas();
    this.initializeSignaturePad();
    this.setupEventListeners();

    setTimeout(() => {
      this.configureCanvasSize();
    }, this.constructor.CONFIG.CANVAS_DELAY);
  }

  // ============================================================================
  // SETUP METHODS
  // ============================================================================

  initializeCanvas() {
    this.canvas = this.canvasTarget;
    this.configureCanvasSize();
  }

  initializeSignaturePad() {
    this.signaturePad = new SignaturePad(
      this.canvas,
      this.constructor.CONFIG.SIGNATURE_PAD_OPTIONS
    );

    this.signaturePad.addEventListener("beginStroke", () => {
      this.updateInputValue();
    });

    this.signaturePad.addEventListener("endStroke", () => {
      this.updateInputValue();
    });
  }

  setupEventListeners() {
    this.setupResizeHandlers();
    this.setupNameFieldListeners();
    this.setupToggleListener();
  }

  // ============================================================================
  // CANVAS OPERATIONS
  // ============================================================================

  configureCanvasSize() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    const containerRect = this.canvas.parentElement.getBoundingClientRect();
    const targetWidth = containerRect.width;
    const targetHeight = this.getResponsiveCanvasHeight();

    this.setCanvasDimensions(targetWidth, targetHeight, ratio);
  }

  setCanvasDimensions(width, height, ratio) {
    this.canvas.width = width * ratio;
    this.canvas.height = height * ratio;

    this.canvas.style.width = width + "px";
    this.canvas.style.height = height + "px";

    const ctx = this.canvas.getContext("2d");
    ctx.scale(ratio, ratio);
  }

  getResponsiveCanvasHeight() {
    const { BREAKPOINTS, CANVAS_HEIGHTS } = this.constructor.CONFIG;

    if (window.innerWidth >= BREAKPOINTS.MD) return CANVAS_HEIGHTS.MD;
    if (window.innerWidth >= BREAKPOINTS.SM) return CANVAS_HEIGHTS.SM;
    return CANVAS_HEIGHTS.XS;
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  setupResizeHandlers() {
    this.resizeTimeout = null;
    this.boundHandleResize = this.handleResize.bind(this);

    const { useResizeObserver } = this.constructor.CONFIG.RESIZE_OPTIONS;

    if (useResizeObserver && window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.boundHandleResize);
      this.resizeObserver.observe(this.element);
    } else {
      window.addEventListener("resize", this.boundHandleResize);
      window.addEventListener("orientationchange", this.boundHandleResize);

      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", this.boundHandleResize);
      }
    }
  }

  setupNameFieldListeners() {
    const nameFields = document.querySelectorAll(
      'input[name*="[first_name]"], input[name*="[last_name]"], input[name*="first_name"], input[name*="last_name"]'
    );

    nameFields.forEach((field) => {
      field.addEventListener("input", () => {
        this.updateSignatureText();
      });
    });
  }

  setupToggleListener() {
    this.element.addEventListener("ui--toggle-group:change", (event) => {
      this.handleToggleChange(event.detail.value);
    });
  }

  handleResize() {
    const containerRect = this.canvas.parentElement.getBoundingClientRect();
    if (containerRect.width === 0 || containerRect.height === 0) {
      return;
    }

    this.debounceResize();
  }

  debounceResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.performResize();
    }, this.constructor.CONFIG.RESIZE_DEBOUNCE);
  }

  handleToggleChange(mode) {
    if (mode === this.currentMode) return;

    this.switchMode(mode);
  }

  // ============================================================================
  // MODE MANAGEMENT
  // ============================================================================

  switchMode(newMode) {
    if (!this.isValidMode(newMode)) {
      return;
    }
    const { MODES } = this.constructor.CONFIG;

    if (newMode === MODES.DRAW) {
      this.enterDrawMode();
    } else if (newMode === MODES.ADOPT) {
      this.enterAdoptMode();
    }
  }

  isValidMode(mode) {
    const { MODES } = this.constructor.CONFIG;
    return Object.values(MODES).includes(mode);
  }

  enterDrawMode() {
    this.currentMode = this.constructor.CONFIG.MODES.DRAW;
    this.hideConsentSection();
    this.signaturePad.on();
    this.canvas.style.cursor = "crosshair";
    this.showClearButton();
    this.clearCanvas();
  }

  enterAdoptMode() {
    this.currentMode = this.constructor.CONFIG.MODES.ADOPT;
    this.signaturePad.off();
    this.canvas.style.cursor = "default";
    this.hideClearButton();
    this.showConsentSection();
    this.updateSignatureText();
  }

  showConsentSection() {
    if (this.hasConsentSectionTarget) {
      this.consentSectionTarget.classList.remove("hidden");
      if (this.hasConsentCheckboxTarget) {
        this.consentCheckboxTarget.required = true;
      }
    }
  }

  hideConsentSection() {
    if (this.hasConsentSectionTarget) {
      this.consentSectionTarget.classList.add("hidden");
      if (this.hasConsentCheckboxTarget) {
        this.consentCheckboxTarget.checked = false;
        this.consentCheckboxTarget.required = false;
      }
    }
  }

  showClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.remove("hidden");
    }
  }

  hideClearButton() {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.add("hidden");
    }
  }

  isDrawMode() {
    return this.currentMode === this.constructor.CONFIG.MODES.DRAW;
  }

  isAdoptMode() {
    return this.currentMode === this.constructor.CONFIG.MODES.ADOPT;
  }

  getModeConfig() {
    const { MODES } = this.constructor.CONFIG;

    return {
      isDraw: this.isDrawMode(),
      isAdopt: this.isAdoptMode(),
      current: this.currentMode,
      clearButtonVisible: this.hasClearButtonTarget
        ? !this.clearButtonTarget.classList.contains("hidden")
        : false,
    };
  }

  performResize() {
    this.canvas.parentElement.offsetHeight;
    this.configureCanvasSize();
    this.signaturePad.clear();

    this.restoreContentAfterResize();
  }

  restoreContentAfterResize() {
    if (this.isAdoptMode()) {
      this.updateSignatureText();
    }
  }

  clearCanvas() {
    this.signaturePad.clear();
    this.inputTarget.value = "";
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  updateInputValue() {
    if (this.signaturePad.isEmpty()) {
      this.inputTarget.value = "";
    } else {
      const dataURL = this.signaturePad.toDataURL();
      this.inputTarget.value = dataURL;
    }
  }

  updateSignatureText() {
    if (!this.isAdoptMode()) return;

    const fullName = this.getFormData().fullName;
    this.updateConsentText(fullName);
    this.clearCanvas();

    if (fullName) {
      this.writeTextToCanvas(fullName);
    }
  }

  getFormData() {
    const firstName = this.getFirstName();
    const lastName = this.getLastName();
    const fullName = `${firstName} ${lastName}`.trim();

    return { firstName, lastName, fullName };
  }

  getFirstName() {
    return (
      document.querySelector('input[name*="[first_name]"], input[name*="first_name"]')?.value || ""
    );
  }

  getLastName() {
    return (
      document.querySelector('input[name*="[last_name]"], input[name*="last_name"]')?.value || ""
    );
  }

  updateConsentText(fullName) {
    if (this.hasConsentTextTarget) {
      const text = `I consent to use my typed name "${fullName}" as my legal electronic signature for this document. I understand this has the same legal effect as a handwritten signature.`;
      this.consentTextTarget.textContent = text;
    }
  }

  writeTextToCanvas(text, options = {}) {
    const ctx = this.canvas.getContext("2d");
    const rect = this.canvas.getBoundingClientRect();
    const { TEXT_OPTIONS } = this.constructor.CONFIG;

    const settings = {
      x: rect.width / 2,
      y: rect.height / 2,
      fontSize: Math.min(rect.width, rect.height) * TEXT_OPTIONS.fontSizeRatio,
      fontFamily: TEXT_OPTIONS.fontFamily,
      color: TEXT_OPTIONS.color,
      textAlign: TEXT_OPTIONS.textAlign,
      textBaseline: TEXT_OPTIONS.textBaseline,
      ...options,
    };

    ctx.save();
    ctx.font = `${settings.fontSize}px ${settings.fontFamily}`;
    ctx.fillStyle = settings.color;
    ctx.textAlign = settings.textAlign;
    ctx.textBaseline = settings.textBaseline;
    ctx.fillText(text, settings.x, settings.y);
    ctx.restore();

    this.updateCanvasInputValue();
  }

  updateCanvasInputValue() {
    if (this.isAdoptMode()) {
      const dataURL = this.canvas.toDataURL();
      this.inputTarget.value = dataURL;
    } else {
      this.updateInputValue();
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  disconnect() {
    this.cleanupResizeHandlers();
    this.cleanupSignaturePad();
  }

  cleanupResizeHandlers() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else if (this.boundHandleResize) {
      window.removeEventListener("resize", this.boundHandleResize);
      window.removeEventListener("orientationchange", this.boundHandleResize);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", this.boundHandleResize);
      }
    }
  }

  cleanupSignaturePad() {
    if (this.signaturePad) {
      this.signaturePad.off();
    }
  }
}
